import { Suspense, lazy, useContext, type ComponentProps } from "react";
import { PageContextValue } from "../engine/CMSComponent.tsx";
import { isExternal } from "../types.ts";

// Loaded from bc-component-kit on demand. Module scope so the lazy component
// is created once, not on every render.
const LazyKitButton = lazy(() => import("bc-component-kit/dist/components/Button.js"));

type KitButtonProps = ComponentProps<typeof LazyKitButton>;

/**
 * Kit Button with a Suspense boundary: SSR waits for it (onAllReady), and on
 * the client only this button suspends while its chunk loads.
 */
export default function KitButton(props: KitButtonProps) {
  return (
    <Suspense fallback={null}>
      <LazyKitButton {...props} />
    </Suspense>
  );
}

export function ChevronRight({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`cms-icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/**
 * Link attributes for CMS hrefs: "#contact" points back to the home page when
 * rendered on another page, and external URLs open in a new tab.
 */
export function useLink(href: string | undefined) {
  const { isHome } = useContext(PageContextValue);
  if (!href) return {};
  const resolved = href.startsWith("#") && !isHome ? `/${href}` : href;
  return isExternal(resolved)
    ? { href: resolved, target: "_blank", rel: "noreferrer" }
    : { href: resolved };
}
